import { useRef } from 'react';
import { DateTime } from 'luxon';
import i18n from 'i18next';
import { CSSObjectWithLabel } from 'react-select';
import { Formik, Field, FieldArray, ArrayHelpers, FormikHelpers } from 'formik';

import { Modal, Button, Select, Tooltip, Checkbox, DatePickerField } from 'web-components';
import { Flex, Text } from 'web-styled';
import { useEventListener } from 'web-hooks';

import * as S from './styled';
import { OpenedTooltip } from 'web-types';

// ============================================================================
// Types
// ============================================================================

interface Option {
  value: string | number;
  label: string;
}

type WeeklySchedule = {
  day: number;
  begins_at: DateTime;
  ends_at: DateTime;
};

interface WorkoutEventFormValues {
  repeat?: string;
  person_id?: string;
  personal_workout_id?: string;
  originalId?: string;
  begins_at: DateTime;
  ends_at: DateTime;
  weekly_schedule: WeeklySchedule[];
  repeat_until: DateTime;
  repeat_type: 'never' | 'daily' | 'weekly';
}

interface Props {
  isOpen: boolean;
  clients: Option[];
  workouts: Option[];
  initialValues: WorkoutEventFormValues;
  openedTooltips: OpenedTooltip[];
  onSubmit: (data: WorkoutEventFormValues, actions: FormikHelpers<WorkoutEventFormValues>) => void | Promise<void>;
  onCancelTooltip: () => void;
  onLogActivity: (startDate: DateTime, clientId: number) => void;
  onRequestClose: () => void;
  onUserSelected: (userId: number) => void;
  lockClient?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DAYS = [
  { symbol: 'S', label: 'SUN', dow: 0 },
  { symbol: 'M', label: 'MON', dow: 1 },
  { symbol: 'T', label: 'TUE', dow: 2 },
  { symbol: 'W', label: 'WED', dow: 3 },
  { symbol: 'T', label: 'THU', dow: 4 },
  { symbol: 'F', label: 'FRI', dow: 5 },
  { symbol: 'S', label: 'SAT', dow: 6 },
];

const REPEAT_OPTIONS = [
  { label: 'Never', value: 'never' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
];

const SELECT_STYLES = {
  container: (provided: CSSObjectWithLabel) => ({ ...provided, width: '100%' }),
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    height: 'calc(2.25rem + 9px)',
    borderRadius: '0.25rem',
  }),
  menu: (provided: CSSObjectWithLabel) => ({ ...provided, marginTop: '1px' }),
};

// ============================================================================
// Helper Functions
// ============================================================================

const getDayIndexInSchedule = (schedule: WeeklySchedule[], dayOfWeek: number) => {
  return schedule.findIndex((d) => d.day === dayOfWeek)
}

const sortScheduleByDay = (schedule: WeeklySchedule[]) => {
  return [...schedule].sort((a, b) => {
    const aIndex = DAYS.findIndex((d) => d.dow === a.day)
    const bIndex = DAYS.findIndex((d) => d.dow === b.day)
    return aIndex - bIndex
  })
}

const buildWeeklyEntry = (base: DateTime, dow: number): WeeklySchedule => {
  const luxonDow = base.weekday % 7 // 0–6
  const diff = (dow - luxonDow + 7) % 7

  const begins = base
    .plus({ days: diff })
    .set({
      hour: base.hour,
      minute: base.minute,
      second: 0,
      millisecond: 0,
    })

  return {
    day: dow,
    begins_at: begins,
    ends_at: begins.plus({ hours: 1 }),
  }
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ClientSelectProps {
  clients: Option[];
  selectedClient: Option | undefined;
  isTooltipOpen: boolean;
  isDisabled: boolean;
  onClientChange: (option: unknown) => void;
  onBlur: () => void;
}

const ClientSelect = ({
  clients,
  selectedClient,
  isTooltipOpen,
  isDisabled,
  onClientChange,
  onBlur,
}: ClientSelectProps) => (
  <Tooltip body={i18n.t('tooltip.calendar.schedule.modal')} isOpen={isTooltipOpen}>
    <Flex width="100%" column>
      <S.Label htmlFor="client-select">{i18n.t('client')}</S.Label>
      <Select
        styles={SELECT_STYLES}
        id="client-select"
        value={selectedClient || null}
        onChange={onClientChange}
        onBlur={onBlur}
        name="person_id"
        options={clients}
        isDisabled={isDisabled}
      />
    </Flex>
  </Tooltip>
);

interface TimeSelectProps {
  beginsAt: DateTime;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
}

const TimeSelect = ({ beginsAt, onStartDateChange, onEndDateChange }: TimeSelectProps) => (
  <Flex width="100%" column>
    <S.Label>Time</S.Label>
    <Flex spacing={10}>
      <Field
        name="begins_at"
        dateFormat="MM/dd/yyyy   hh:mm a"
        showTimeSelect
        minDate={new Date()}
        onChange={onStartDateChange}
        component={DatePickerField}
      />
      <Field
        name="ends_at"
        selected={new Date(beginsAt.toISO())}
        dateFormat="MM/dd/yyyy   hh:mm a"
        showTimeSelect
        minDate={new Date(beginsAt.toISO())}
        onChange={onEndDateChange}
        component={DatePickerField}
      />
    </Flex>
  </Flex>
);

interface WorkoutSelectProps {
  workouts: Option[];
  selectedWorkout: Option | undefined;
  onWorkoutChange: (option: unknown) => void;
  onBlur: () => void;
}

const WorkoutSelect = ({ workouts, selectedWorkout, onWorkoutChange, onBlur }: WorkoutSelectProps) => (
  <Flex width="100%" column>
    <S.Label htmlFor="workout-select">{i18n.t('Workout')}</S.Label>
    <Select
      styles={SELECT_STYLES}
      id="workout-select"
      value={selectedWorkout || null}
      onBlur={onBlur}
      onChange={onWorkoutChange}
      name="personal_workout_id"
      options={workouts}
    />
  </Flex>
);

interface RepeatSelectProps {
  repeat: Option | undefined;
  beginsAt: DateTime;
  endsAt: DateTime;
  onRepeatChange: (option: Option) => void;
  onRepeatUntilChange: (date: Date) => void;
  onBlur: () => void;
}

const RepeatSelect = ({ repeat, beginsAt, onRepeatChange, onRepeatUntilChange, onBlur }: RepeatSelectProps) => (
  <Flex width="100%" spacing={10}>
    <Flex width="calc(50% - 5px)" column>
      <S.Label htmlFor="repeat">Repeat</S.Label>
      <Select
        styles={SELECT_STYLES}
        id="repeat_type"
        value={repeat}
        onChange={onRepeatChange}
        onBlur={onBlur}
        name="repeat_type"
        options={REPEAT_OPTIONS}
      />
    </Flex>
    {repeat?.value !== 'never' && (
      <Flex width="calc(50% - 5px)" column>
        <S.Label>Until</S.Label>
        <Field
          name="repeat_until"
          dateFormat="MM/dd/yyyy"
          minDate={new Date(beginsAt.toISO())}
          onChange={onRepeatUntilChange}
          component={DatePickerField}
        />
      </Flex>
    )}
  </Flex>
);

interface DayPickerProps {
  schedule: WeeklySchedule[];
  onDayToggle: (day: typeof DAYS[0]) => void;
}

const DayPicker = ({ schedule, onDayToggle }: DayPickerProps) => (
  <Flex width="100%" column>
    <S.Label>On</S.Label>
    <Flex width="100%" justify="space-between">
      {DAYS.map((day) => {
        const selectedIndex = getDayIndexInSchedule(schedule, day.dow);
        return (
          <S.Day key={day.label} selected={selectedIndex > -1} onClick={() => onDayToggle(day)}>
            {day.symbol}
          </S.Day>
        );
      })}
    </Flex>
  </Flex>
);

interface ScheduleTimesProps {
  schedule: WeeklySchedule[];
  onTimeChange: (index: number, date: Date) => void;
}

const ScheduleTimes = ({ schedule, onTimeChange }: ScheduleTimesProps) => {
  if (schedule.length === 0) return null;

  return (
    <Flex width="100%" column>
      <S.Label>At</S.Label>
      <Flex column spacing={15}>
        {sortScheduleByDay(schedule).map((day) => {
          const formikIndex = getDayIndexInSchedule(schedule, day.day)
          return (
            <Flex key={day.day} align="center" spacing={33}>
              <Flex spacing={12} width="50px" align="center">
                <Checkbox size="middle" checked onChange={() => { }} />
                <Text size="16px" weight="600" color="#666666">
                  {DAYS.find((d) => d.dow === day.day)!.label}
                </Text>
              </Flex>
              <Field
                name={`weekly_schedule[${formikIndex}].begins_at`}
                width="90px"
                height="calc(2.25rem + 2px)"
                showTimeSelect
                showTimeSelectOnly
                showPopperArrow={false}
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                popperModifiers={[
                  {
                    name: 'placeModifier',
                    fn(state: any) {
                      return { ...state, x: 0 };
                    },
                  },
                ]}
                onChange={(date: Date) => onTimeChange(formikIndex, date)}
                component={DatePickerField}
              />
            </Flex>
          )
        })}
      </Flex>
    </Flex>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const NewWorkoutEventModal = ({
  isOpen,
  clients,
  workouts,
  initialValues,
  openedTooltips,
  onSubmit,
  onLogActivity,
  onRequestClose,
  onUserSelected,
  onCancelTooltip,
  lockClient = false,
}: Props) => {
  const targetRef = useRef<HTMLDivElement>(null);

  useEventListener('click', onCancelTooltip, {
    target: targetRef.current,
    disabled: !isOpen,
  });

  const isScheduleTooltipOpen = openedTooltips.some((el) => el.key === 'schedule_workout_form');

  return (
    <Modal
      title={initialValues.originalId ? 'Edit' : i18n.t('schedule.new.workout')}
      isOpen={isOpen}
      contentStyle={{ width: 400 }}
      onRequestClose={onRequestClose}
    >
      <Formik<WorkoutEventFormValues>
        initialValues={{
          ...initialValues,
          repeat_type: 'never',
          weekly_schedule: [],
        }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(formik) => {
          const selectedClient = clients.find(
            (opt) => String(opt.value) === String(formik.values.person_id),
          );
          const selectedWorkout = workouts.find(
            (opt) => String(opt.value) === String(formik.values.personal_workout_id),
          );
          const selectedRepeat = REPEAT_OPTIONS.find((opt) => opt.value === formik.values.repeat_type);

          const isFormValid = !!(
            formik.values.begins_at &&
            formik.values.ends_at &&
            formik.values.personal_workout_id &&
            formik.values.person_id
          );

          // Handler functions
          const handleClientChange = (option: any) => {
            if (option) {
              formik.setFieldValue('person_id', option.value);
              formik.setFieldValue('personal_workout_id', null);
              onUserSelected(Number(option.value));
            }
          };

          const handleWorkoutChange = (option: any) => {
            if (option) {
              formik.setFieldValue('personal_workout_id', option.value);
            }
          };

          const handleStartDateChange = (date: Date) => {
            const start = DateTime.fromJSDate(date)
            formik.setFieldValue('begins_at', start)
            formik.setFieldValue('ends_at', start.plus({ hours: 1 }))
          };

          const handleEndDateChange = (date: Date) => {
            formik.setFieldValue('ends_at', DateTime.fromJSDate(date))
          }

          const handleRepeatChange = (option: any) => {
            formik.setFieldValue('repeat_type', option.value)
            formik.setFieldValue('repeat_until', formik.values.begins_at.endOf('day'))

            if (option.value === 'weekly') {
              const dow = formik.values.begins_at.weekday % 7

              formik.setFieldValue('weekly_schedule', [
                buildWeeklyEntry(formik.values.begins_at, dow),
              ])
            } else {
              formik.setFieldValue('weekly_schedule', [])
            }
          }

          const handleRepeatUntilChange = (date: Date) => {
            formik.setFieldValue('repeat_until', DateTime.fromJSDate(date))
          }

          const handleDayToggle = (arrayHelpers: ArrayHelpers) => (day: typeof DAYS[0]) => {
            const selectedIndex = getDayIndexInSchedule(formik.values.weekly_schedule, day.dow)

            if (selectedIndex > -1) {
              arrayHelpers.remove(selectedIndex)
            } else {
              arrayHelpers.push(
                buildWeeklyEntry(formik.values.begins_at, day.dow)
              )
            }
          }

          const handleScheduleTimeChange = (formikIndex: number, date: Date) => {
            const prev = formik.values.weekly_schedule[formikIndex].begins_at

            const begins = prev.set({
              hour: date.getHours(),
              minute: date.getMinutes(),
            })

            formik.setFieldValue(`weekly_schedule[${formikIndex}].begins_at`, begins)
            formik.setFieldValue(`weekly_schedule[${formikIndex}].ends_at`, begins.plus({ hours: 1 }))
          }

          return (
            <Flex as="form" ref={targetRef} column width="100%" onSubmit={formik.handleSubmit}>
              {!initialValues.originalId && (
                <Flex width="100%" justify="flex-end">
                  <Button
                    appearance="link"
                    onClick={() => onLogActivity(
                      formik.values.begins_at,
                      Number(formik.values.person_id),
                    )}
                  >
                    Log Activity instead
                  </Button>
                </Flex>
              )}

              <ClientSelect
                clients={clients}
                selectedClient={selectedClient}
                isTooltipOpen={isScheduleTooltipOpen}
                isDisabled={lockClient}
                onClientChange={handleClientChange}
                onBlur={formik.handleBlur}
              />

              <TimeSelect
                beginsAt={formik.values.begins_at}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />

              <WorkoutSelect
                workouts={workouts}
                selectedWorkout={selectedWorkout}
                onWorkoutChange={handleWorkoutChange}
                onBlur={formik.handleBlur}
              />

              <RepeatSelect
                repeat={selectedRepeat}
                beginsAt={formik.values.begins_at}
                endsAt={formik.values.ends_at}
                onRepeatChange={handleRepeatChange}
                onRepeatUntilChange={handleRepeatUntilChange}
                onBlur={formik.handleBlur}
              />

              {formik.values.repeat_type === 'weekly' && (
                <FieldArray name="weekly_schedule">
                  {(arrayHelpers: ArrayHelpers) => (
                    <Flex width="100%" column>
                      <DayPicker schedule={formik.values.weekly_schedule} onDayToggle={handleDayToggle(arrayHelpers)} />
                      <ScheduleTimes schedule={formik.values.weekly_schedule} onTimeChange={handleScheduleTimeChange} />
                    </Flex>
                  )}
                </FieldArray>
              )}

              <S.Footer>
                <hr />
                <Flex width="100%" justify="flex-end" spacing={20}>
                  <Button ghost appearance="danger" onClick={onRequestClose}>
                    Cancel
                  </Button>
                  <Button type="submit" appearance="primary" isLoading={formik.isSubmitting} isDisabled={formik.isSubmitting || !isFormValid}>
                    Schedule
                  </Button>
                </Flex>
              </S.Footer>
            </Flex>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NewWorkoutEventModal;
